// Sugalarm — resilient ApiResult wrapper (cycle 76d).
//
// Several screens load critical state from a *single* fetch fired once on
// mount (the active-member list, the app-wide settings). Before this
// module those one-shot fetches silently gave up on the first failure —
// so a transient blip (the flaky Fly `ord` edge resets seen in the
// field: timeouts / "connection reset by peer") on initial load stranded
// the member name (`—` / `MEMBER`) and the Settings pane ("Loading…")
// permanently, until the device was reloaded by hand.
//
// `retryResult` re-attempts those calls with exponential backoff, but
// ONLY for transient failure kinds. Permanent kinds (auth / forbidden /
// not_found / validation) will never succeed on retry, so they surface
// immediately. Kept tiny + dependency-free per the cycle-9 tech lock-in.

import type { ApiErrorKind, ApiResult } from "./types";

// Failure kinds worth retrying — transient network / infrastructure
// blips. Everything else is a hard "no" that retries can't fix.
const TRANSIENT_KINDS: ReadonlySet<ApiErrorKind> = new Set<ApiErrorKind>([
  "network",
  "server",
  "rate_limit",
  "unknown",
]);

export interface RetryOpts {
  /** Max total attempts, including the first. Default 5. */
  attempts?: number;
  /** Base backoff in ms; doubles each retry, capped at `maxDelayMs`. */
  baseDelayMs?: number;
  /** Upper bound on a single backoff wait. Default 8000. */
  maxDelayMs?: number;
  /** Abort to stop retrying (unmount / signed-out / superseded call). */
  signal?: AbortSignal;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

/**
 * Run an `ApiResult`-returning thunk, retrying transient failures with
 * exponential backoff. Returns the last `ApiResult` — `ok` on the first
 * success, or the final failure once attempts are exhausted, a permanent
 * error is hit, or the signal aborts.
 */
export async function retryResult<T>(
  fn: () => Promise<ApiResult<T>>,
  opts: RetryOpts = {},
): Promise<ApiResult<T>> {
  const attempts = Math.max(1, opts.attempts ?? 5);
  const base = opts.baseDelayMs ?? 500;
  const maxDelay = opts.maxDelayMs ?? 8000;

  let last = await fn();
  for (let attempt = 1; attempt < attempts; attempt++) {
    if (last.ok) return last;
    if (!TRANSIENT_KINDS.has(last.error.kind)) return last;
    if (opts.signal?.aborted) return last;
    await sleep(Math.min(base * 2 ** (attempt - 1), maxDelay), opts.signal);
    if (opts.signal?.aborted) return last;
    last = await fn();
  }
  return last;
}
